import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight,
  ShieldCheck,
  Truck,
  Users,
  Award,
  Target,
  Building2
} from "lucide-react";

const values = [
  {
    icon: ShieldCheck,
    title: "Regulatory Compliance",
    description: "We distribute only FDA-cleared and regulatory-compliant diagnostic products, ensuring your facility meets all applicable standards."
  },
  {
    icon: Truck,
    title: "Supply Reliability",
    description: "Our distribution network is designed to provide consistent, dependable supply to healthcare facilities of all sizes."
  },
  {
    icon: Users,
    title: "Professional Focus",
    description: "We serve healthcare professionals exclusively, understanding the unique requirements of clinical environments."
  },
  {
    icon: Award,
    title: "Quality Assurance",
    description: "Every product we distribute undergoes rigorous quality checks to ensure performance and reliability."
  }
];

const stats = [
  { value: "3", label: "Product Lines" },
  { value: "100%", label: "FDA Compliant" },
  { value: "B2B", label: "Professional Only" },
  { value: "USA", label: "Distribution" }
];

export default function AboutPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="caption-text text-sky-600 mb-4 block">About Us</span>
              <h1 className="heading-primary mb-6" data-testid="about-headline">
                Professional Diagnostics Distribution
              </h1>
              <p className="body-large" data-testid="about-description">
                ClearPath Rapid Diagnostics is a professional distributor of point-of-care diagnostic tests, 
                serving clinics, urgent care centers, occupational health providers, and healthcare facilities 
                across the United States.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://healgen.com/wp-content/uploads/2025/09/hCG-Pregnancy.png"
                alt="HealGen diagnostic products"
                className="w-full aspect-[4/3] object-cover"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center" data-testid={`about-stat-${idx}`}>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="caption-text text-sky-600 mb-4 block">Our Mission</span>
            <h2 className="heading-secondary mb-6" data-testid="about-mission-headline">
              Supporting Healthcare Through Reliable Diagnostics
            </h2>
            <p className="body-large">
              We believe that access to reliable, high-quality diagnostic tests is fundamental to 
              effective patient care. Our mission is to provide healthcare professionals with the 
              diagnostic tools they need, backed by dependable supply and professional service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card-feature">
              <div className="flex items-center gap-4 mb-4">
                <div className="feature-icon">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Our Focus
                </h3>
              </div>
              <p className="body-default">
                We specialize exclusively in point-of-care diagnostic testing products. This focused 
                approach allows us to maintain deep expertise in the products we distribute and provide 
                knowledgeable support to our customers.
              </p>
            </div>
            <div className="card-feature">
              <div className="flex items-center gap-4 mb-4">
                <div className="feature-icon">
                  <Building2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Who We Serve
                </h3>
              </div>
              <p className="body-default">
                Our customers include physician offices, urgent care centers, occupational health clinics, 
                community health centers, and other healthcare facilities requiring reliable point-of-care 
                diagnostic solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="caption-text text-sky-600 mb-4 block">Our Values</span>
            <h2 className="heading-secondary" data-testid="about-values-headline">
              What Guides Our Work
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="text-center" data-testid={`about-value-${idx}`}>
                <div className="feature-icon mx-auto mb-4">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="body-default text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="caption-text text-sky-600 mb-4 block">Our Commitment</span>
                <h2 className="heading-secondary mb-6">
                  Professional Service, Every Time
                </h2>
                <p className="body-large mb-6">
                  As a B2B distributor, we understand that your facility depends on reliable supply and 
                  responsive service. We are committed to:
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="body-default">Distributing only regulatory-compliant products</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="body-default">Maintaining consistent inventory availability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="body-default">Providing responsive customer support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="body-default">Offering competitive pricing for healthcare facilities</span>
                  </li>
                </ul>
                <Link to="/contact" data-testid="about-contact-btn">
                  <Button className="bg-sky-600 hover:bg-sky-700 text-white">
                    Contact Our Team
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img 
                  src="https://healgen.com/wp-content/uploads/2025/10/Fecal-Occult-Blood_Transferrin-Rapid-Test.png"
                  alt="HealGen diagnostic testing solutions"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Partner With ClearPath?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Contact us to discuss your facility's diagnostic testing needs and learn about our products and pricing.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="about-cta-contact">
              <Button className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base">
                Contact Sales
              </Button>
            </Link>
            <Link to="/products" data-testid="about-cta-products">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-base">
                View Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
